/**
 * US postal code validation and normalization.
 *
 * A ZIP is stored as bare digits — 5, or 9 for ZIP+4 — and the hyphen is added back
 * only when rendering. The API rejects anything else outright rather than correcting
 * it, so a typo has to be caught here to reach the operator as a field error instead
 * of a failed request.
 */

const ZIP_LENGTH = 5
const ZIP_PLUS_FOUR_LENGTH = 9

export const REQUIRED_ERROR = 'ZIP code is required.'
export const LENGTH_ERROR = 'Enter a 5-digit ZIP or a 9-digit ZIP+4.'

/** Digits only, so an operator may type the ZIP+4 hyphen. */
export const toPostalDigits = (value: string): string => value.replace(/\D/g, '')

/**
 * Returns an empty string when the postal code is usable, or the reason it is not.
 *
 * Empty input reports as required, so a single validator covers both the presence
 * and the format rules a form field needs.
 */
export const validatePostalCode = (value: string): string => {
  if (!value.trim()) {
    return REQUIRED_ERROR
  }

  const digits = toPostalDigits(value)
  if (digits.length !== ZIP_LENGTH && digits.length !== ZIP_PLUS_FOUR_LENGTH) {
    return LENGTH_ERROR
  }

  return ''
}

/**
 * Formats a stored ZIP as 78701 or 78701-1234 for display.
 *
 * Anything that is not 5 or 9 digits comes back trimmed but otherwise untouched —
 * this is a display helper, so it must never obscure what is actually stored.
 */
export const formatPostalCode = (value: string): string => {
  const digits = toPostalDigits(value)
  if (digits.length === ZIP_PLUS_FOUR_LENGTH) {
    return `${digits.slice(0, ZIP_LENGTH)}-${digits.slice(ZIP_LENGTH)}`
  }
  if (digits.length === ZIP_LENGTH) {
    return digits
  }
  return value.trim()
}
